import { type Client } from "@xmpp/client"
import FileUploadApi from "../api/fileUploadApi"
import { sendFile } from "../reducers/chatsSliceThunks"
import type { AppDispatch } from "../reducers/store"
import ChatHelpers from "./chatHelpers"
import i18n from "./i18n/i18n"

/** The driverbot mis-orders a message and its attachment when they arrive back
 * to back, so the parts are spaced out. */
const DELAY_BETWEEN_MESSAGE_PARTS = 200

const sleeper = (ms: number) => new Promise((r) => setTimeout(r, ms))

type UploadDeps = {
  client: Client
  dispatch: AppDispatch
}

/**
 * Whether the text goes out as the caption of the first attachment instead of
 * as its own message. Only in the agent chat: a file plus the request about it
 * ("send this to Kowalski", a pasted screenshot and its question) is one
 * request, and as two messages the agent answers before it has the file.
 * Every other chat keeps them apart, because the driverbot needs them apart
 * (see DELAY_BETWEEN_MESSAGE_PARTS).
 */
export const sendsTextAsAttachmentCaption = (
  chatJid: string,
  text: string,
  files: ArrayLike<File>,
): boolean =>
  ChatHelpers.isAIChatRoom(chatJid) && files.length > 0 && !/^\s*$/.test(text)

const uploadAndSendFile = async (
  file: File,
  urls: [string, string],
  { client, dispatch }: UploadDeps,
  caption?: string,
) => {
  const [get, put] = urls
  const response = await fetch(put, { method: "PUT", body: file })
  // A rejected PUT resolves like any other response. Posting the GET url
  // regardless would put an audio bubble in the chat pointing at a file the
  // store never accepted, for the driver as well as for us.
  if (!response.ok) {
    throw new Error(
      `Upload of "${file.name}" failed with ${response.status} ${response.statusText}`,
    )
  }
  await dispatch(sendFile(file, get, client, caption))
}

/** Uploads each file to a freshly requested slot and posts its url to the chat.
 * A file that cannot be stored raises a toast and is skipped; the rest still
 * go out. A `caption` rides on the first file only. */
export const uploadAndSendMultipleFiles = async (
  files: FileList,
  deps: UploadDeps,
  { caption }: { caption?: string } = {},
) => {
  if (files.length === 0) return
  // Space the attachments off the text message that preceded them.
  await sleeper(DELAY_BETWEEN_MESSAGE_PARTS)

  for (const [index, file] of Array.from(files).entries()) {
    try {
      const urls = await FileUploadApi.requestSlot(
        deps.client,
        file.name,
        file.size.toString(),
        file.type,
      )
      await uploadAndSendFile(
        file,
        urls,
        deps,
        index === 0 ? caption : undefined,
      )
      await sleeper(DELAY_BETWEEN_MESSAGE_PARTS)
    } catch (e) {
      console.error(e)
      window.analytics.track("FileUploadError", { error: e })
      window.appToaster.show({
        message: i18n.t("chat.upload.failed", { name: file.name }),
        intent: "danger",
        timeout: 3000,
      })
    }
  }
}
